/**
 * Normalizacao de ambientes para o documento de impressao + payload de
 * finalizar (salvar no historico). Usado pelo editor ao vivo e pela lista
 * salva — uma fonte unica de verdade do "shape" do documento.
 */
import { HOLES_MODULE_ID, HOLES_MODULE_NAME, itemParaPayload } from "./parseProjeto";

const STATUS_MAP = {
  production: "Produção", producao: "Produção", draft: "Rascunho",
  rascunho: "Rascunho", fechado: "Fechado", closed: "Fechado",
};

export function statusLabel(s) {
  return STATUS_MAP[(s || "").toLowerCase()] || s || "—";
}

/**
 * Quantidade de ferragem para EXIBIÇÃO. Ferragem nem sempre é "peça": fita de
 * borda vem em metros (6.04). Mostra sem casas quando é inteiro (24) e com até
 * 3 casas quando fracionário (6,04) — no padrão pt-BR.
 */
export function fmtQt(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

/**
 * Prefixo do moduleId de módulos criados manualmente ("Novo módulo"). Garante
 * id único/deterministico por NOME (mesmo nome agrupa junto) e SEM colidir com
 * o mid de módulos reais do DinaBox (que nunca começam com este prefixo).
 */
export const MODULO_MANUAL_PREFIX = "manualmod:";

/** id de módulo manual a partir do nome digitado. */
export function idModuloManual(nome) {
  return MODULO_MANUAL_PREFIX + String(nome || "").trim();
}

/** Módulo sem código curto (mid): holes ou módulo manual. Não infere por id===nome. */
export function moduloSemCodigo(moduleId) {
  return moduleId === HOLES_MODULE_ID || String(moduleId || "").startsWith(MODULO_MANUAL_PREFIX);
}

/** Valor sentinela no <select> de módulo que abre o campo "novo módulo". */
export const NOVO_MODULO = "__novo__";

/**
 * Resolve { moduleId, moduleName, moduleOrder } a partir do form do item manual
 * e dos módulos existentes do ambiente. Retorna null se "Novo módulo" sem nome.
 */
export function resolverModuloManual(form, modulos) {
  if (form.moduleId === NOVO_MODULO) {
    const nome = (form.novoModulo || "").trim();
    if (!nome) return null;
    return { moduleId: idModuloManual(nome), moduleName: nome, moduleOrder: 9998 };
  }
  const moduleId = form.moduleId || (modulos[0]?.moduleId ?? HOLES_MODULE_ID);
  const grupo = modulos.find((g) => g.moduleId === moduleId);
  return {
    moduleId,
    moduleName: grupo ? grupo.moduleName : HOLES_MODULE_NAME,
    moduleOrder: grupo ? grupo.moduleOrder : 9999,
  };
}

/**
 * Editor ao vivo (contexto) -> shape do documento.
 * `ambientes`: [{ projectId, raw(meta), items }].
 */
export function ambientesLiveParaDocumento(ambientes) {
  return (ambientes || []).map((a) => ({
    projectId: a.projectId,
    nome: a.raw?.project_description || "Ambiente",
    cliente: a.raw?.project_customer_name || a.raw?.project_client_name || "",
    autor: a.raw?.project_author_name || "",
    status: statusLabel(a.raw?.project_status),
    items: a.items || [],
  }));
}

/**
 * Lista salva (detalhe do backend) -> shape do documento + UI de conferencia.
 * Reconstroi `moduleOrder` por ordem de encontro dentro de cada ambiente
 * (holes = 9999), e mantem os campos de conferencia.
 */
export function ambientesSalvosParaDocumento(lista) {
  const projMap = new Map();
  (lista?.projetos_dinabox || []).forEach((p) => projMap.set(String(p.project_id), p));

  const ambMap = new Map();
  (lista?.itens || []).forEach((it) => {
    const pid = String(it.projeto_dinabox_id || "");
    if (!ambMap.has(pid)) {
      const meta = projMap.get(pid) || {};
      ambMap.set(pid, {
        projectId: pid || "—",
        nome: it.ambiente_nome || meta.nome || "Ambiente",
        cliente: meta.cliente || "",
        autor: meta.autor || "",
        status: meta.status || "",
        _itens: [],
        _ordemModulo: new Map(),
      });
    }
    ambMap.get(pid)._itens.push(it);
  });

  return Array.from(ambMap.values()).map((amb) => {
    let nextOrder = 0;
    const items = amb._itens.map((it) => {
      const moduleId = it.modulo_id || HOLES_MODULE_ID;
      let moduleOrder;
      if (moduleId === HOLES_MODULE_ID) {
        moduleOrder = 9999;
      } else if (amb._ordemModulo.has(moduleId)) {
        moduleOrder = amb._ordemModulo.get(moduleId);
      } else {
        moduleOrder = nextOrder;
        amb._ordemModulo.set(moduleId, nextOrder);
        nextOrder += 1;
      }
      return {
        id: it.id,
        moduleId,
        moduleName: it.modulo_nome || HOLES_MODULE_NAME,
        moduleOrder,
        category: it.categoria,
        name: it.nome,
        manufacturer: it.fabricante,
        description: it.descricao,
        dimensions: it.dimensoes,
        // Number(): quantidade é decimal (fita em metros) e o resumo SOMA os
        // valores — se vier string do backend, `+` viraria concatenação.
        qt: Number(it.quantidade) || 0,
        hidden: it.oculto,
        manual: it.manual,
        conferido: it.conferido,
        conferidoPorNome: it.conferido_por_nome,
      };
    });
    return {
      projectId: amb.projectId,
      nome: amb.nome,
      cliente: amb.cliente,
      autor: amb.autor,
      status: amb.status,
      items,
    };
  });
}

/** Editor ao vivo (contexto) -> payload de POST /listas (finalizar). */
export function montarPayloadLista(ambientes) {
  const cliente =
    ambientes[0]?.raw?.project_customer_name ||
    ambientes[0]?.raw?.project_client_name ||
    "Cliente";
  const ambNomes = ambientes
    .map((a) => a.raw?.project_description || "#" + a.projectId)
    .join(", ");
  const hoje = new Date().toLocaleDateString("pt-BR");
  const titulo = `${cliente} — ${ambNomes} (${hoje})`.slice(0, 290);

  const projetos = ambientes.map((a) => ({
    project_id: a.projectId,
    nome: a.raw?.project_description || "",
    cliente: a.raw?.project_customer_name || a.raw?.project_client_name || "",
    autor: a.raw?.project_author_name || "",
    status: a.raw?.project_status || "",
  }));

  let ordem = 0;
  const itens = [];
  ambientes.forEach((a) => {
    a.items.forEach((it) => {
      itens.push(itemParaPayload(a, it, ordem));
      ordem += 1;
    });
  });

  return { titulo, observacao: "", projetos_dinabox: projetos, itens };
}

/** Nome de arquivo do PDF a partir dos ambientes do documento. */
export function nomeArquivoPdf(ambientesDoc) {
  const limpa = (s) => String(s || "").replace(/[^a-zA-Z0-9À-ú _-]/g, "").trim();
  const cliente = limpa(ambientesDoc[0]?.cliente).slice(0, 30) || "Cliente";
  const ambs = ambientesDoc
    .map((a) => limpa(a.nome || a.projectId))
    .join("-")
    .slice(0, 40);
  const hoje = new Date().toLocaleDateString("pt-BR").replace(/\//g, "-");
  return `Lista_Compras_${cliente}_${ambs}_${hoje}.pdf`.replace(/\s+/g, "_");
}
